/* assets/littleg-upload-draft.js — FAMILY-NOTICE-DRAFT-RUNTIME-08
 * Owner: explicit local-private Upload draft capture, restoration and safe isolated-flow exit.
 * Persistence API owner: assets/littleg-family-data.js.
 * Deadline read/restore owner: assets/littleg-upload-notice-contracts.js.
 * Confirm success is observed through littleg:upload-transport-success; confirmNotice is never replaced here.
 * Safe-exit rule: flush the current synchronous local-private snapshot before native href navigation/pagehide.
 * Boundary: no direct deadline DOM reads/writes, storage keys, File/Blob persistence,
 * backend mutation, history routing or duplicate U01–U06 containers.
 */
(function littleGUploadDraftRuntime(window, document) {
  'use strict';

  var VERSION = '2026-08-08-notice-draft-08';
  var ROOT_SELECTOR = '[data-littleg-upload-workspace]';
  var existingRuntime = window.LittleGUploadDraftRuntime;

  if (existingRuntime && existingRuntime.VERSION === VERSION && typeof existingRuntime.boot === 'function') {
    existingRuntime.boot(document);
    return;
  }
  if (existingRuntime && typeof existingRuntime.dispose === 'function') existingRuntime.dispose();

  var bindings = new WeakMap();
  var restoring = false;
  var restorePlan = null;
  var restoreRoot = null;

  function clean(value) { return String(value == null ? '' : value).trim(); }
  function array(value) { return Array.isArray(value) ? value : []; }
  function familyData() {
    var owner = window.LittleGFamilyData;
    return owner && typeof owner.saveNoticeDraft === 'function' ? owner : null;
  }
  function deadlineOwner() { return window.LittleGNoticeDeadlines || null; }
  function clone(value) { try { return JSON.parse(JSON.stringify(value)); } catch (_) { return null; } }
  function rootsIn(scope) {
    var target = scope || document;
    var roots = [];
    if (target.matches && target.matches(ROOT_SELECTOR)) roots.push(target);
    if (target.querySelectorAll) {
      Array.prototype.forEach.call(target.querySelectorAll(ROOT_SELECTOR), function (root) {
        if (roots.indexOf(root) === -1) roots.push(root);
      });
    }
    return roots;
  }
  function isRestoring(root) { return restoring && restoreRoot === root; }
  function clearRestore(root) {
    if (root && restoreRoot !== root) return;
    restoring = false;
    restorePlan = null;
    restoreRoot = null;
  }
  function announce(root, message) {
    var node = root && root.querySelector('[data-upload-announcer]');
    if (node) node.textContent = clean(message);
  }
  function localeRoot() {
    var root = clean(window.Shopify && window.Shopify.routes && window.Shopify.routes.root);
    if (!root) return '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }
  function exitCopy() {
    var lang = clean(document.documentElement.lang).toLowerCase();
    if (lang.indexOf('en') === 0) return { label: 'Back to LittleG', short: 'LittleG' };
    if (lang.indexOf('zh-cn') === 0 || lang.indexOf('zh-sg') === 0 || lang.indexOf('hans') !== -1) {
      return { label: '返回小G宝', short: '小G宝' };
    }
    return { label: '返回小G寶', short: '小G寶' };
  }
  function ensureFlowExit(root) {
    if (!root || root.querySelector('[data-upload-flow-exit]')) return;
    var copy = exitCopy();
    var nav = document.createElement('nav');
    nav.className = 'lgu-flow-nav';
    nav.setAttribute('aria-label', copy.label);
    nav.setAttribute('data-upload-flow-nav', VERSION);
    var link = document.createElement('a');
    link.className = 'lgu-flow-exit';
    link.href = localeRoot();
    link.setAttribute('data-upload-flow-exit', 'true');
    link.setAttribute('aria-label', copy.label);
    var arrow = document.createElement('span');
    arrow.className = 'lgu-flow-exit__arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '←';
    var label = document.createElement('span');
    label.className = 'lgu-flow-exit__label';
    label.textContent = copy.short;
    link.append(arrow, label);
    nav.appendChild(link);
    root.insertBefore(nav, root.firstChild || null);
  }
  function fieldValue(fields, key) {
    var field = array(fields).find(function (candidate) { return candidate && candidate.key === key && candidate.guardian_state !== 'removed'; });
    return field ? clean(field.value) : '';
  }
  function click(root, selector) {
    var node = root.querySelector(selector);
    if (!node || node.disabled) return false;
    node.click();
    return true;
  }
  function collectReview(root) {
    return Array.prototype.map.call(root.querySelectorAll('[data-notice-field]'), function (row) {
      var input = row.querySelector('[data-field-value]');
      return {
        key: clean(row.getAttribute('data-field-key')),
        label: clean(row.querySelector('[data-field-label]') && row.querySelector('[data-field-label]').textContent),
        value: clean(input && input.value),
        recognition_state: clean(row.getAttribute('data-recognition-state')) || 'not_found',
        guardian_state: clean(row.getAttribute('data-guardian-state')) || 'pending',
        source_excerpt: clean(row.querySelector('[data-source-excerpt]') && row.querySelector('[data-source-excerpt]').textContent)
      };
    }).filter(function (field) { return field.key; });
  }
  function collectDeadlines(root) {
    var owner = deadlineOwner();
    if (!owner || typeof owner.getAll !== 'function') {
      root.setAttribute('data-upload-deadline-owner-state', 'unavailable');
      return [];
    }
    root.setAttribute('data-upload-deadline-owner-state', 'ready');
    return clone(owner.getAll()) || [];
  }
  function collectPacking(root) {
    return Array.prototype.map.call(root.querySelectorAll('[data-packing-item]'), function (row) {
      return {
        id: clean(row.getAttribute('data-packing-item-id')),
        group: clean(row.getAttribute('data-packing-item-source')) || 'custom',
        name: clean(row.querySelector('[data-packing-item-label]') && row.querySelector('[data-packing-item-label]').textContent),
        note: clean(row.querySelector('[data-packing-item-note]') && row.querySelector('[data-packing-item-note]').textContent)
      };
    }).filter(function (item) { return item.name; });
  }
  function collectChecklist(root) {
    return Array.prototype.map.call(root.querySelectorAll('[data-prep-item]'), function (row) {
      var checkbox = row.querySelector('[data-prep-item-toggle]');
      return {
        id: clean(row.getAttribute('data-prep-item-id') || row.getAttribute('data-prep-index')),
        group: clean(row.getAttribute('data-prep-group')) || 'custom',
        name: clean(row.querySelector('[data-prep-item-label]') && row.querySelector('[data-prep-item-label]').textContent),
        note: clean(row.querySelector('[data-prep-item-note]') && row.querySelector('[data-prep-item-note]').textContent),
        checked: !!(checkbox && checkbox.checked)
      };
    }).filter(function (item) { return item.name; });
  }
  function collectManualItems(root) {
    return Array.prototype.map.call(root.querySelectorAll('[data-manual-items] li span'), function (node) { return clean(node.textContent); }).filter(Boolean);
  }
  function currentAction() {
    var engine = window.LittleGActionEngine;
    return engine && typeof engine.getCurrent === 'function' ? engine.getCurrent() : null;
  }
  function meaningful(draft) {
    return !!(
      array(draft.review_fields).length || array(draft.deadlines).length || array(draft.packing_items).length ||
      array(draft.checklist).length || array(draft.manual_items).length || clean(draft.manual_activity_name) ||
      clean(draft.manual_activity_date) || draft.review_response || draft.packing_response || draft.action ||
      draft.confirmation_recovery_required === true || clean(draft.backend_record_id)
    );
  }
  function snapshot(root) {
    var owner = familyData();
    if (!owner || isRestoring(root) || !document.documentElement.contains(root)) return null;
    var existing = owner.getNoticeDraft() || {};
    var state = clean(root.getAttribute('data-upload-state')) || 'empty';
    if (state === 'records') {
      var recoveryRequired =
        root.getAttribute('data-record-persistence') === 'backend_confirmed' &&
        root.getAttribute('data-record-local-mirror') === 'failed';
      if (!recoveryRequired) {
        owner.clearNoticeDraft('confirmed');
        updateRestoreControl(root, null);
        return null;
      }
    }
    var draft = Object.assign({}, existing, {
      schema: 'littleg_action_draft_v2',
      state: state,
      step: clean(root.getAttribute('data-upload-active-step')) || 'u01',
      customer_state: clean(root.getAttribute('data-customer-state')),
      file_metadata: {
        name: clean(root.querySelector('[data-upload-file-name]') && root.querySelector('[data-upload-file-name]').textContent),
        size: clean(root.querySelector('[data-upload-file-size]') && root.querySelector('[data-upload-file-size]').textContent)
      },
      manual_activity_name: clean(root.querySelector('[data-manual-activity-name]') && root.querySelector('[data-manual-activity-name]').value),
      manual_activity_date: clean(root.querySelector('[data-manual-activity-date]') && root.querySelector('[data-manual-activity-date]').value),
      manual_items: collectManualItems(root),
      review_fields: collectReview(root),
      deadlines: collectDeadlines(root),
      packing_items: collectPacking(root),
      checklist: collectChecklist(root),
      action: currentAction()
    });
    if (!meaningful(draft)) return null;
    var saved = owner.saveNoticeDraft(draft);
    updateRestoreControl(root, saved);
    return saved;
  }
  function flush(root) {
    var binding = bindings.get(root);
    if (binding) {
      window.clearTimeout(binding.saveTimer);
      binding.saveTimer = 0;
    }
    return snapshot(root);
  }
  function scheduleSave(root) {
    var binding = bindings.get(root);
    if (!binding || isRestoring(root)) return;
    window.clearTimeout(binding.saveTimer);
    binding.saveTimer = window.setTimeout(function () {
      binding.saveTimer = 0;
      snapshot(root);
    }, 250);
  }
  function updateRestoreControl(root, draft) {
    var control = root.querySelector('[data-upload-action="recent"],[data-upload-action="restore-draft"]');
    if (!control) return;
    var available = !!draft;
    control.hidden = !available;
    control.disabled = !available;
    control.setAttribute('aria-disabled', available ? 'false' : 'true');
    control.setAttribute('data-upload-action', available ? 'restore-draft' : 'recent');
    var recovery = !!(draft && draft.confirmation_recovery_required === true);
    control.textContent = recovery ? '查看已保存結果' : '繼續上次進度';
    control.setAttribute(
      'aria-label',
      available
        ? (recovery ? '查看後端已確認保存的結果' : '繼續上次未完成的行動')
        : '目前沒有未完成行動'
    );
  }
  function wrapParseAndPacking() {
    var transport = window.LittleGUploadTransport;
    if (!transport || transport.__noticeDraftWrapped === VERSION) return;
    ['parseNotice', 'generatePackingList'].forEach(function (method) {
      var original = transport[method];
      if (typeof original !== 'function') return;
      transport[method] = function wrappedTransport(input) {
        var activePlan = restoreRoot && document.documentElement.contains(restoreRoot) ? restorePlan : null;
        if (!activePlan && restorePlan) clearRestore();
        if (activePlan && method === 'parseNotice' && activePlan.review_response) {
          activePlan.used_review = true;
          return Promise.resolve(clone(activePlan.review_response));
        }
        if (activePlan && method === 'generatePackingList' && activePlan.packing_response) {
          activePlan.used_packing = true;
          return Promise.resolve(clone(activePlan.packing_response));
        }
        return Promise.resolve(original.call(transport, input)).then(function (response) {
          var owner = familyData();
          var draft = owner && owner.getNoticeDraft() || {};
          if (owner && method === 'parseNotice') owner.saveNoticeDraft(Object.assign({}, draft, { review_response: clone(response) }));
          if (owner && method === 'generatePackingList') owner.saveNoticeDraft(Object.assign({}, draft, { packing_response: clone(response) }));
          return response;
        });
      };
    });
    transport.__noticeDraftWrapped = VERSION;
  }
  function restoreManual(root, draft) {
    restoreRoot = root;
    restoring = true;
    click(root, '[data-upload-action="manual"]');
    var name = root.querySelector('[data-manual-activity-name]');
    var date = root.querySelector('[data-manual-activity-date]');
    var fields = draft.review_fields || (draft.review_response && draft.review_response.data && draft.review_response.data.notice && draft.review_response.data.notice.fields) || [];
    if (name) name.value = clean(draft.manual_activity_name || fieldValue(fields, 'activity_name'));
    if (date) date.value = clean(draft.manual_activity_date || fieldValue(fields, 'activity_date'));
    var names = array(draft.checklist).map(function (item) { return clean(item.name); }).filter(Boolean);
    if (!names.length) names = array(draft.packing_items).map(function (item) { return clean(item.name); }).filter(Boolean);
    if (!names.length) names = array(draft.manual_items).map(clean).filter(Boolean);
    var input = root.querySelector('[data-manual-item-input]');
    names.forEach(function (item) {
      if (!input) return;
      input.value = item;
      click(root, '[data-upload-action="add-item"]');
    });
    var form = root.querySelector('[data-upload-manual-form]');
    clearRestore(root);
    if (form) {
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }
  function restoreStructured(root, draft) {
    if (!window.navigator.onLine || !draft.review_response || typeof window.DataTransfer !== 'function' || typeof window.File !== 'function') {
      restoreManual(root, draft);
      return;
    }
    var input = root.querySelector('[data-upload-input="library"]');
    if (!input) {
      restoreManual(root, draft);
      return;
    }
    restoreRoot = root;
    restorePlan = clone(draft);
    restoring = true;
    try {
      var transfer = new window.DataTransfer();
      transfer.items.add(new window.File(['LittleG local draft'], 'littleg-draft.png', { type: 'image/png' }));
      input.files = transfer.files;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      restoring = false;
      if (!click(root, '[data-upload-action="parse"]')) {
        clearRestore(root);
        restoreManual(root, draft);
      }
    } catch (_) {
      clearRestore(root);
      restoreManual(root, draft);
    }
  }
  function applyDeadlines(root, draft) {
    var owner = deadlineOwner();
    var deadlines = array(draft && draft.deadlines);
    if (!deadlines.length) {
      root.setAttribute('data-upload-deadline-owner-state', owner ? 'ready' : 'not_required');
      return true;
    }
    if (!owner || typeof owner.restore !== 'function') {
      root.setAttribute('data-upload-deadline-owner-state', 'unavailable');
      announce(root, '期限資料暫時未能恢復，已停止自動前進。');
      root.dispatchEvent(new CustomEvent('littleg:notice-draft-restore-blocked', {
        bubbles: true,
        detail: { reason: 'deadline_owner_unavailable' }
      }));
      return false;
    }
    owner.restore(deadlines, { source: 'draft_restore', emit: true });
    root.setAttribute('data-upload-deadline-owner-state', 'ready');
    return true;
  }
  function applyChecklist(root, draft) {
    var expected = array(draft.checklist);
    Array.prototype.forEach.call(root.querySelectorAll('[data-prep-item]'), function (row) {
      var name = clean(row.querySelector('[data-prep-item-label]') && row.querySelector('[data-prep-item-label]').textContent);
      var match = expected.find(function (item) { return clean(item.name) === name; });
      var checkbox = row.querySelector('[data-prep-item-toggle]');
      if (!match || !checkbox) return;
      checkbox.checked = !!match.checked;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
  function restoreAction(draft) {
    var engine = window.LittleGActionEngine;
    if (draft && draft.action && engine && typeof engine.setCurrent === 'function') engine.setCurrent(draft.action, 'draft-restore');
  }
  function advanceRestore(root, state) {
    if (!restorePlan || restoreRoot !== root) return;
    var target = clean(restorePlan.state);
    if (state === 'review') {
      if (!applyDeadlines(root, restorePlan)) {
        clearRestore(root);
        return;
      }
      restoreAction(restorePlan);
      if (restorePlan.packing_response && target !== 'review') {
        click(root, '[data-upload-action="review-continue"]');
        return;
      }
    }
    if (state === 'packing' && (target === 'prep' || target === 'confirm')) {
      click(root, '[data-upload-action="packing-continue"]');
      return;
    }
    if (state === 'prep') {
      applyChecklist(root, restorePlan);
      if (target === 'confirm') {
        click(root, '[data-upload-action="prep-continue"]');
        return;
      }
    }
    if (state === target || (target === 'file_selected' && state === 'review')) {
      clearRestore(root);
      root.setAttribute('data-upload-draft-restored', 'true');
      root.dispatchEvent(new CustomEvent('littleg:notice-draft-restored', { bubbles: true, detail: { state: state } }));
    }
  }
  function bind(root) {
    if (!root || bindings.has(root)) return;

    var owner = familyData();
    var binding = {
      saveTimer: 0,
      skipNextStateSave: false,
      onInput: function () { scheduleSave(root); },
      onChange: function () { scheduleSave(root); },
      onClick: function (event) {
        var target = event.target && event.target.closest ? event.target : null;
        var exit = target && target.closest('[data-upload-flow-exit]');
        if (exit && root.contains(exit)) {
          flush(root);
          return;
        }
        var actionNode = target && target.closest('[data-upload-action]');
        var action = actionNode && clean(actionNode.getAttribute('data-upload-action'));
        if (action === 'start-new-notice' && root.contains(actionNode)) {
          window.clearTimeout(binding.saveTimer);
          binding.saveTimer = 0;
          binding.skipNextStateSave = true;
          var resetOwner = familyData();
          if (resetOwner && typeof resetOwner.clearNoticeDraft === 'function') {
            resetOwner.clearNoticeDraft('start_new_notice');
          }
          updateRestoreControl(root, null);
          clearRestore(root);
          return;
        }
        var restore = target && target.closest('[data-upload-action="restore-draft"]');
        if (restore && root.contains(restore)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          var currentOwner = familyData();
          var draft = currentOwner && currentOwner.getNoticeDraft();
          if (draft && draft.confirmation_recovery_required === true) {
            root.dispatchEvent(new CustomEvent('littleg:upload-confirmation-recovery', {
              bubbles: true,
              detail: { draft: clone(draft) }
            }));
            root.setAttribute('data-upload-draft-restored', 'confirmation_recovery');
          } else if (draft) {
            restoreStructured(root, draft);
          }
          return;
        }
        scheduleSave(root);
      },
      onStateChange: function (event) {
        var state = clean(event.detail && event.detail.state);
        if (binding.skipNextStateSave && state === 'empty') {
          binding.skipNextStateSave = false;
          return;
        }
        if (restorePlan && restoreRoot === root) advanceRestore(root, state);
        else scheduleSave(root);
      },
      onPageHide: function () { flush(root); }
    };

    bindings.set(root, binding);
    root.setAttribute('data-upload-draft-bound', VERSION);
    ensureFlowExit(root);
    wrapParseAndPacking();
    updateRestoreControl(root, owner && owner.getNoticeDraft());
    root.addEventListener('input', binding.onInput);
    root.addEventListener('change', binding.onChange);
    root.addEventListener('click', binding.onClick, true);
    root.addEventListener('littleg:upload-state-change', binding.onStateChange);
    window.addEventListener('pagehide', binding.onPageHide);
  }
  function unbind(root) {
    var binding = bindings.get(root);
    if (!binding) return;
    window.clearTimeout(binding.saveTimer);
    root.removeEventListener('input', binding.onInput);
    root.removeEventListener('change', binding.onChange);
    root.removeEventListener('click', binding.onClick, true);
    root.removeEventListener('littleg:upload-state-change', binding.onStateChange);
    window.removeEventListener('pagehide', binding.onPageHide);
    root.removeAttribute('data-upload-draft-bound');
    root.querySelector('[data-upload-flow-nav]')?.remove();
    bindings.delete(root);
    clearRestore(root);
  }
  function boot(scope) {
    wrapParseAndPacking();
    rootsIn(scope).forEach(bind);
  }
  function destroy(scope) {
    rootsIn(scope).forEach(unbind);
  }

  function confirmationFromTransportDetail(detail) {
    var response =
      detail &&
      detail.response &&
      typeof detail.response === 'object'
        ? detail.response
        : {};

    var data =
      response.data &&
      typeof response.data === 'object' &&
      !Array.isArray(response.data)
        ? response.data
        : {};

    if (
      data.confirmation &&
      typeof data.confirmation === 'object' &&
      !Array.isArray(data.confirmation)
    ) {
      return data.confirmation;
    }

    if (
      data.record &&
      typeof data.record === 'object' &&
      !Array.isArray(data.record)
    ) {
      return data.record;
    }

    return data;
  }

  function handleTransportSuccess(event) {
    var detail = event && event.detail || {};

    if (detail.method !== 'confirmNotice') return;

    var confirmation = confirmationFromTransportDetail(detail);
    var persistence = clean(confirmation.persistence);
    var localMirrorSaved = confirmation.local_mirror_saved === true;
    var durableLocalRecord = persistence === 'local_only' || localMirrorSaved;
    var owner = familyData();

    rootsIn(document).forEach(function (root) {
      if (durableLocalRecord) {
        if (owner && typeof owner.clearNoticeDraft === 'function') owner.clearNoticeDraft('confirmed');
        updateRestoreControl(root, null);
        return;
      }

      var existing = owner && typeof owner.getNoticeDraft === 'function' ? owner.getNoticeDraft() || {} : {};
      var recoveryDraft = Object.assign({}, existing, {
        schema: 'littleg_action_draft_v2',
        state: 'records',
        step: 'u06',
        confirmation_recovery_required: true,
        backend_record_id: clean(
          confirmation.record_id ||
          confirmation.result_id ||
          confirmation.local_record_id
        ),
        backend_confirmed_at: clean(confirmation.confirmed_at),
        local_mirror_state: 'failed',
        updated_at: new Date().toISOString()
      });
      var savedRecoveryDraft = owner && typeof owner.saveNoticeDraft === 'function' ? owner.saveNoticeDraft(recoveryDraft) : null;
      updateRestoreControl(root, savedRecoveryDraft);
    });
  }

  function handleReady() { boot(document); }
  function handleSectionLoad(event) { boot(event.target || document); }
  function handleSectionUnload(event) { destroy(event.target || document); }
  function dispose() {
    destroy(document);
    document.removeEventListener('DOMContentLoaded', handleReady);
    document.removeEventListener('littleg:upload-transport-success', handleTransportSuccess);
    document.removeEventListener('shopify:section:load', handleSectionLoad);
    document.removeEventListener('shopify:section:unload', handleSectionUnload);
  }

  window.LittleGUploadDraftRuntime = Object.freeze({
    VERSION: VERSION,
    boot: boot,
    destroy: destroy,
    dispose: dispose,
    flush: function (root) {
      var target = root || document.querySelector(ROOT_SELECTOR);
      return target ? flush(target) : null;
    }
  });

  document.addEventListener('littleg:upload-transport-success', handleTransportSuccess);
  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', handleReady, { once: true });
  else boot(document);
})(window, document);